import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';

import { Button } from './Button';
import { Callout } from './Callout';

/**
 * What a person sees when a screen crashes while rendering, instead of a white page.
 *
 * Without it React unmounts the whole tree on an uncaught render error: the navigation
 * goes with it and the only way out is to guess that reloading helps. `Sentry.ErrorBoundary`
 * reports the crash (a no-op without a DSN) and paints the fallback; the stack trace goes
 * to Sentry, never to the screen.
 *
 * `page` is the last resort around the whole application (no layout left to sit in); the
 * default sits inside the layout, around the current screen, so the menu keeps working.
 * The layout remounts it per path, so moving to another screen clears the failure.
 */
export function ScreenErrorBoundary({
  children,
  page = false,
}: {
  children: ReactNode;
  page?: boolean;
}) {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => <CrashNotice page={page} onRetry={() => props.resetError()} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export function CrashNotice({ page, onRetry }: { page: boolean; onRetry: () => void }) {
  const notice = (
    <Callout tone="danger" title="Esta pantalla ha fallado">
      <p>
        Lo que tenías guardado sigue guardado. Vuelve a intentarlo; si vuelve a pasar, recarga la
        página y, si sigue igual, avisa a quien administra la aplicación.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={onRetry}>
          Volver a intentarlo
        </Button>
        <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
          Recargar la página
        </Button>
      </div>
    </Callout>
  );
  if (!page) return notice;
  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-4">{notice}</div>
  );
}
